import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  BidIconTypeEnum,
  BidMessageEnum,
  BidMessageTypeEnum,
  BidStatus,
  IBid,
  IBidCreation,
  IBidPanelRowDetails,
  IBidResultDetail,
  ICorridorDetail,
  ISubmissionHistoryItems,
} from '.';
import { BidClient } from '../core/clients';
import { axiosToObservable } from '../core/clients/axiosToObservable';

export class BidService {
  getAllCorridors = (): Observable<ICorridorDetail[]> => {
    return axiosToObservable(BidClient.get<string[]>('referenceData/all/corridors')).pipe(
      map((corridors: string[]) => {
        return corridors
          ? corridors.map((x) => this.mapToCorridorDetail(x))
          : ([] as ICorridorDetail[]);
      })
    );
  };

  getBidDetailsByAuctionIds = (
    auctionIds: string[]
  ): Observable<IBidResultDetail[]> => {
    return axiosToObservable(BidClient.post<IBidResultDetail[]>('bid/latest/result', auctionIds)).pipe(
      map((bidResultDetails: IBidResultDetail[]) => {
        return bidResultDetails;
      })
    );
  };

  getSubmissionHistoryByDocumentId = (
    documentId?: string
  ): Observable<ISubmissionHistoryItems[]> => {
    return axiosToObservable(BidClient.get<ISubmissionHistoryItems[]>(`bid/submission/history/${documentId}`)).pipe(
      map((submissionHistoryItems: ISubmissionHistoryItems[]) => {
        return submissionHistoryItems;
      })
    );
  };

  bidSubmission(bid?: IBidCreation): Observable<IBidResultDetail> {
    return axiosToObservable(BidClient.post<IBidResultDetail>('bid', bid)).pipe(
      map((bidResultDetail: IBidResultDetail) => {
        return bidResultDetail;
      })
    );
  }

  allQuantityZeroForAPageInGivenBids = (bids: IBid[]) => {
    let allQuantityZero = false;
    for (const bid of bids) {
      if(!(bid.status === BidStatus.Accepted || bid.status === BidStatus.Amended)){
      const bidRows = bid.bidPanelRowDetails;
      allQuantityZero = true;
      if (bidRows) {
        for (const bid of bidRows) {
          if (bid.quantity > 0) {
            allQuantityZero = false;
            break;
          }
        }
        if (allQuantityZero) {
          return allQuantityZero;
        }
      }
    }
    }
    return allQuantityZero;
  };

  validateAllRows = (
    bidPanelRowDetails: IBidPanelRowDetails[],
    setAlertMessage?: Function,
    enableBidSubmission?: Function
  ) => {
    let isValid = true;
    for (const bidPanelRow of bidPanelRowDetails) {
      if (
        bidPanelRow &&
        (isNaN(bidPanelRow?.quantity) ||
          isNaN(bidPanelRow?.price) ||
          bidPanelRow?.quantity < 0 ||
          bidPanelRow?.price < 0)
      ) {
        isValid = false;
        break;
      }
    }
    if (!isValid) {
      if (setAlertMessage) {
        setAlertMessage(
          true,
          BidMessageTypeEnum.error,
          BidIconTypeEnum.error,
          BidMessageEnum.OnlyPositiveNumericsAllowed
        );
        if (enableBidSubmission) {
          enableBidSubmission(false);
        }
      }
    } else {
      if (setAlertMessage) {
        setAlertMessage(
          false,
          BidMessageTypeEnum.error,
          BidIconTypeEnum.error,
          BidMessageEnum.OnlyPositiveNumericsAllowed
        );
        if (enableBidSubmission) {
          enableBidSubmission(true);
        }
      }
    }
    return isValid;
  };

  sortProductDetails = (
    props?: IBidPanelRowDetails[]
  ): IBidPanelRowDetails[] => {
    let sortedProps: IBidPanelRowDetails[] = [];
    if (props) {
      sortedProps = props.sort((h1, h2) => {
        let hour1 = Number(h1.productHour.substring(0, 2));
        let hour2 = Number(h2.productHour.substring(0, 2));
        if (hour1 > hour2) {
          return 1;
        }
        if (hour1 < hour2) {
          return -1;
        }
        return 0;
      });
    }
    return sortedProps;
  };

  validateDecimalPlaces = (bids: IBid[], noOfDecimalPlaces: number) => {
    for (const bid of bids) {
      if (bid.bidPanelRowDetails) {
        for (const row of bid.bidPanelRowDetails) {
          const price = Number(row.price);
          if (this.findNoOfDecimalPlaces(price) > noOfDecimalPlaces) {
            return false;
          }
        }
      }
    }
    return true;
  };

  private mapToCorridorDetail = (corridorCode: string) => {
    const corridorWithInterconnector = corridorCode.split('-');
    if (corridorWithInterconnector.length > 2) {
      return {
        corridorKey: corridorCode,
        interconnector: corridorWithInterconnector[0],
        corridor: `${corridorWithInterconnector[1]}-${corridorWithInterconnector[2]}`,
      } as ICorridorDetail;
    }
    return {
      corridor: corridorCode,
      corridorKey: corridorCode,
    } as ICorridorDetail;
  };

  private findNoOfDecimalPlaces = (number: number): number => {
    if (Number.isInteger(number)) {
      return 0;
    }
    const noOfDecimalPlaces = number.toString().split('.')[1].length;
    return noOfDecimalPlaces;
  };
}
